/**
 * @module services/catalogs/harvardLibraryService
 * @description Service for searching books via Harvard LibraryCloud API
 *
 * Harvard LibraryCloud provides access to over 20 million metadata records
 * from Harvard Library's collections, including Alma, JSTOR Forum, and
 * ArchivesSpace finding aids.
 *
 * @see https://api.lib.harvard.edu/v2/items
 * @see https://harvardwiki.atlassian.net/wiki/spaces/LibraryStaffDoc/pages/43286729/LibraryCloud
 */

import type { ItemData, ItemType, Creator, CreatorType } from '@types';
import type { BaseCatalogRecord, BaseCatalogSearchResponse } from './types';
import { getCreatorTypesForItemType, getFieldsForItemType } from '@/data/itemTypes';
import { delay, extractPageCount, capitalizePublisher } from './utils';

// =============================================================================
// TYPES
// =============================================================================

/** Harvard Library search result */
export interface HarvardLibraryRecord extends BaseCatalogRecord {
  /** Thumbnail URL if available */
  thumbnail?: string;
}

/** Harvard Library search response */
export type HarvardLibrarySearchResponse = BaseCatalogSearchResponse<HarvardLibraryRecord>;

/** Search options */
export interface HarvardLibrarySearchOptions {
  startIndex?: number;
  maxResults?: number;
  searchType?: 'keyword' | 'title' | 'author' | 'isbn';
}

// =============================================================================
// INTERNAL TYPES - MODS Format
// =============================================================================

/**
 * Note: Harvard's API returns JSON with:
 * - "#text" for text content (not "_")
 * - "@attr" for XML attributes (e.g., "@type", "@encoding")
 *
 * The parsing functions handle both conventions dynamically.
 */

/** Text value can be string or object with text content */
type TextValue = string | { _: string } | { '#text': string };

interface ModsTitleInfo {
  title?: TextValue;
  nonSort?: TextValue;
  subTitle?: TextValue;
  partNumber?: TextValue;
  partName?: TextValue;
}

interface ModsName {
  namePart?: TextValue | TextValue[];
  role?: {
    roleTerm?: TextValue | TextValue[];
  };
  type?: string;
  '@type'?: string;
}

interface ModsOriginInfo {
  dateIssued?: TextValue | TextValue[];
  dateCreated?: TextValue | TextValue[];
  publisher?: string | string[];
  place?:
    | {
        placeTerm?: TextValue;
      }
    | Array<{ placeTerm?: TextValue }>;
  edition?: string;
}

interface ModsPhysicalDescription {
  extent?: TextValue | TextValue[];
  form?: TextValue | TextValue[];
}

/** Identifier with type and value */
interface ModsIdentifier {
  _?: string;
  '#text'?: string;
  type?: string;
  '@type'?: string;
}

interface ModsRecord {
  titleInfo?: ModsTitleInfo | ModsTitleInfo[];
  name?: ModsName | ModsName[];
  originInfo?: ModsOriginInfo | ModsOriginInfo[];
  physicalDescription?: ModsPhysicalDescription;
  abstract?: TextValue | TextValue[];
  note?: TextValue | TextValue[];
  subject?: Array<{
    topic?: TextValue | TextValue[];
    geographic?: TextValue | TextValue[];
    temporal?: TextValue | TextValue[];
  }>;
  language?: {
    languageTerm?: TextValue | TextValue[];
  };
  genre?: TextValue | TextValue[];
  typeOfResource?: TextValue;
  identifier?: ModsIdentifier | ModsIdentifier[];
  recordInfo?: {
    recordIdentifier?: TextValue;
  };
  location?: Array<{
    url?: TextValue | TextValue[];
    physicalLocation?: TextValue;
    shelfLocator?: string;
  }>;
  relatedItem?: Array<{
    type?: string;
    '@type'?: string;
    titleInfo?: ModsTitleInfo;
  }>;
}

interface HarvardApiResponse {
  pagination?: {
    numFound?: number;
    start?: number;
    limit?: number;
  };
  items?: {
    mods?: ModsRecord | ModsRecord[];
  };
}

// =============================================================================
// CONSTANTS
// =============================================================================

const HARVARD_API_BASE = 'https://api.lib.harvard.edu/v2/items.json';
const MIN_REQUEST_INTERVAL = 1000; // 1 second between requests

let lastRequestTime = 0;

// =============================================================================
// TYPE GUARDS - Safe extraction from MODS union types
// =============================================================================

/**
 * Type guard for objects with underscore property (MODS value objects - XML convention)
 * Some XML-to-JSON converters use "_" for text content
 */
function hasUnderscoreValue(obj: unknown): obj is { _: string } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '_' in obj &&
    typeof (obj as { _: unknown })._ === 'string'
  );
}

/**
 * Type guard for objects with #text property (JSON from Harvard API)
 * Harvard's API uses "#text" for text content and "@attr" for attributes
 */
function hasTextValue(obj: unknown): obj is { '#text': string } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '#text' in obj &&
    typeof (obj as { '#text': unknown })['#text'] === 'string'
  );
}

/**
 * Type guard for objects with either _ or #text property
 */
function hasAnyTextValue(obj: unknown): obj is { _: string } | { '#text': string } {
  return hasUnderscoreValue(obj) || hasTextValue(obj);
}

/**
 * Extract text value from object with _ or #text property
 */
function getTextValue(obj: unknown): string | null {
  if (hasUnderscoreValue(obj)) {
    return obj._;
  }
  if (hasTextValue(obj)) {
    return obj['#text'];
  }
  return null;
}

/**
 * Get type attribute from object (handles both "type" and "@type")
 */
function getTypeAttr(obj: unknown): string | undefined {
  if (typeof obj !== 'object' || obj === null) {
    return undefined;
  }
  const o = obj as Record<string, unknown>;
  if (typeof o['@type'] === 'string') {
    return o['@type'];
  }
  if (typeof o['type'] === 'string') {
    return o['type'];
  }
  return undefined;
}

/** Type guard for MODS value objects with type property */
function isTypedValue(
  obj: unknown
): obj is { _: string; type?: string } | { '#text': string; '@type'?: string } {
  return hasAnyTextValue(obj);
}

/**
 * Safely extract string value from MODS union types
 * Handles: string | string[] | { _: string }[] | { '#text': string }[] | etc.
 */
function extractStringValue(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (typeof first === 'string') {
      return first;
    }
    const textVal = getTextValue(first);
    if (textVal !== null) {
      return textVal;
    }
  }
  return getTextValue(value);
}

/**
 * Safely extract all string values from MODS array types
 */
function extractStringValues(value: unknown): string[] {
  const results: string[] = [];

  if (typeof value === 'string') {
    results.push(value);
  } else if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string') {
        results.push(item);
      } else {
        const textVal = getTextValue(item);
        if (textVal !== null) {
          results.push(textVal);
        }
      }
    }
  } else {
    const textVal = getTextValue(value);
    if (textVal !== null) {
      results.push(textVal);
    }
  }

  return results;
}

/**
 * Extract text-type value from MODS languageTerm or roleTerm arrays
 * Prefers entries with type="text" over type="code"
 */
function extractTextTypeValue(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    // First, look for text type (handles both @type and type)
    for (const item of value) {
      if (isTypedValue(item)) {
        const typeAttr = getTypeAttr(item);
        if (typeAttr === 'text') {
          return getTextValue(item);
        }
      }
    }
    // Fallback to first item without type or any item not of type "code"
    for (const item of value) {
      if (typeof item === 'string') {
        return item;
      }
      if (hasAnyTextValue(item)) {
        const typeAttr = getTypeAttr(item);
        if (!typeAttr || typeAttr !== 'code') {
          return getTextValue(item);
        }
      }
    }
    // Last resort: any value
    const first = value[0];
    if (typeof first === 'string') {
      return first;
    }
    return getTextValue(first);
  }

  return getTextValue(value);
}

// =============================================================================
// DATA CLEANING HELPERS
// =============================================================================

/**
 * Remove life dates from name parts (e.g., "Emmanuel 1983-" → "Emmanuel")
 * Handles patterns like:
 * - "1983-", "1931-1994", "1900-"
 * - "1963 June 7-" (with month and day)
 * - "1963 June 7-2020 March 15" (full date range)
 * Also removes trailing period unless it's an initial (e.g., "Cedric J.")
 */
function cleanNameDates(name: string): string {
  let cleaned = name
    // Full date patterns: "1963 June 7-" or "1963 June 7-2020 March 15"
    .replace(/,?\s*\d{4}\s+\w+\s+\d{1,2}-(\d{4}\s+\w+\s+\d{1,2})?\.?$/, '')
    // Simple year patterns: ", 1983-" or "1931-1994"
    .replace(/,?\s*\d{4}-\d{0,4}\.?$/, '')
    .replace(/\s+\d{4}-\d{0,4}\.?$/, '')
    .trim();

  // Remove trailing period unless it's an initial (single letter + period)
  // "Guy-Claude." → "Guy-Claude" but "Cedric J." stays as is
  if (cleaned.endsWith('.') && !/\s[A-Za-z]\.$/.test(cleaned)) {
    cleaned = cleaned.slice(0, -1).trim();
  }

  return cleaned;
}

/**
 * Clean publisher string (remove trailing comma/period and capitalize)
 */
function cleanPublisher(publisher: string): string {
  const cleaned = publisher.replace(/[,.]$/, '').trim();
  return capitalizePublisher(cleaned);
}

/**
 * Clean date string
 * - Remove trailing period
 * - Extract year from dates like "febbraio 2019" or "March 2020"
 * - Handle "c2019" (circa) -> "2019"
 */
function cleanDate(date: string): string {
  const cleaned = date.replace(/\.$/, '').trim();

  // If date contains month name + year, extract just the year
  // Matches: "febbraio 2019", "March 2020", "janvier 2018", etc.
  const monthYearMatch = cleaned.match(/^[a-zA-ZÀ-ÿ]+\s+(\d{4})$/);
  if (monthYearMatch) {
    return monthYearMatch[1];
  }

  // Handle "c2019" or "c. 2019" (circa)
  const circaMatch = cleaned.match(/^c\.?\s*(\d{4})$/);
  if (circaMatch) {
    return circaMatch[1];
  }

  // Handle "[2019]" (uncertain date)
  const bracketMatch = cleaned.match(/^\[(\d{4})\]$/);
  if (bracketMatch) {
    return bracketMatch[1];
  }

  return cleaned;
}

// =============================================================================
// SERVICE
// =============================================================================

class HarvardLibraryService {
  /**
   * Search the Harvard LibraryCloud catalog
   */
  async search(
    query: string,
    options: HarvardLibrarySearchOptions = {}
  ): Promise<HarvardLibrarySearchResponse> {
    const { startIndex = 0, maxResults = 10, searchType = 'keyword' } = options;

    if (!query.trim()) {
      return { totalResults: 0, records: [] };
    }

    // Rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
    }
    lastRequestTime = Date.now();

    // Build URL
    const url = new URL(HARVARD_API_BASE);

    switch (searchType) {
      case 'title':
        url.searchParams.set('title', query);
        break;
      case 'author':
        url.searchParams.set('name', query);
        break;
      case 'isbn':
        url.searchParams.set('identifier', query.replace(/[-\s]/g, ''));
        break;
      default:
        url.searchParams.set('q', query);
    }

    url.searchParams.set('start', String(startIndex));
    url.searchParams.set('limit', String(Math.min(maxResults, 250)));

    // Retry logic
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url.toString());

        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : (attempt + 1) * 3000;
          await delay(waitTime);
          continue;
        }

        if (!response.ok) {
          throw new Error(`Harvard Library request failed: ${response.status}`);
        }

        const data: HarvardApiResponse = await response.json();
        return this.parseResponse(data);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < maxRetries - 1) {
          await delay((attempt + 1) * 1000);
        }
      }
    }

    throw lastError || new Error('Harvard Library: Search failed.');
  }

  /**
   * Search by ISBN
   */
  async searchByISBN(isbn: string): Promise<HarvardLibraryRecord | null> {
    const result = await this.search(isbn, { maxResults: 1, searchType: 'isbn' });
    return result.records[0] || null;
  }

  /**
   * Parse Harvard LibraryCloud API response
   */
  private parseResponse(data: HarvardApiResponse): HarvardLibrarySearchResponse {
    const totalResults = data.pagination?.numFound || 0;
    const records: HarvardLibraryRecord[] = [];

    if (!data.items?.mods) {
      return { totalResults, records };
    }

    const modsRecords = Array.isArray(data.items.mods) ? data.items.mods : [data.items.mods];

    for (const mods of modsRecords) {
      try {
        const record = this.parseModsRecord(mods);
        if (record) {
          records.push(record);
        }
      } catch {
        // Skip unparseable records
      }
    }

    return { totalResults, records };
  }

  /**
   * Parse a single MODS record
   */
  private parseModsRecord(mods: ModsRecord): HarvardLibraryRecord | null {
    const recordId = this.extractRecordId(mods);
    if (!recordId) {
      return null;
    }

    const itemType = this.determineItemType(mods);
    const title = this.extractTitle(mods);
    const creators = this.extractCreators(mods, itemType);
    const originInfo = this.extractOriginInfo(mods);
    const identifiers = this.extractIdentifiers(mods);
    const abstract = this.extractAbstract(mods);
    const subjects = this.extractSubjects(mods);
    const urls = this.extractUrls(mods);

    const item: Partial<ItemData> = {
      itemType,
      title: title || '[Sans titre]',
      creators,
    };

    const validFields = getFieldsForItemType(itemType);

    if (validFields.includes('date') && originInfo.date) {
      item.date = originInfo.date;
    }
    if (validFields.includes('publisher') && originInfo.publisher) {
      item.publisher = originInfo.publisher;
    }
    if (validFields.includes('place') && originInfo.place) {
      item.place = originInfo.place;
    }
    if (validFields.includes('edition') && originInfo.edition) {
      item.edition = originInfo.edition;
    }
    if (validFields.includes('numPages') && originInfo.extent) {
      const pageCount = extractPageCount(originInfo.extent);
      if (pageCount) {
        item.numPages = pageCount;
      }
    }
    if (validFields.includes('ISBN') && identifiers.isbn) {
      item.ISBN = identifiers.isbn;
    }
    if (validFields.includes('ISSN') && identifiers.issn) {
      item.ISSN = identifiers.issn;
    }
    if (validFields.includes('abstractNote') && abstract) {
      item.abstractNote = abstract;
    }
    if (validFields.includes('language') && originInfo.language) {
      item.language = originInfo.language;
    }
    if (validFields.includes('url') && urls.accessUrl) {
      item.url = urls.accessUrl;
    }
    if (subjects.length > 0) {
      item.tags = subjects.map((s) => ({ tag: s }));
    }

    return {
      id: recordId,
      item,
      thumbnail: urls.thumbnail,
      catalogUrl: `https://id.lib.harvard.edu/alma/${recordId}/catalog`,
    };
  }

  // ==========================================================================
  // MODS Extractors
  // ==========================================================================

  private extractRecordId(mods: ModsRecord): string | null {
    if (!mods.recordInfo?.recordIdentifier) {
      return null;
    }

    const recordId = mods.recordInfo.recordIdentifier;
    if (typeof recordId === 'string') {
      return recordId;
    }
    // Handle both { _: string } and { '#text': string } formats
    const textVal = getTextValue(recordId);
    if (textVal) {
      return textVal;
    }
    return null;
  }

  private determineItemType(mods: ModsRecord): ItemType {
    const typeOfResource = mods.typeOfResource;
    let resourceType = '';

    if (typeof typeOfResource === 'string') {
      resourceType = typeOfResource.toLowerCase();
    } else if (typeOfResource && typeof typeOfResource === 'object') {
      // Handle both { _: string } and { '#text': string } formats
      const textVal = getTextValue(typeOfResource);
      resourceType = (textVal || '').toLowerCase();
    }

    const genres = this.extractGenres(mods);
    const genreStr = genres.join(' ').toLowerCase();

    if (resourceType.includes('text')) {
      if (genreStr.includes('thesis') || genreStr.includes('dissertation')) {
        return 'thesis';
      }
      if (genreStr.includes('article') || genreStr.includes('periodical')) {
        return 'journalArticle';
      }
      if (genreStr.includes('newspaper')) {
        return 'newspaperArticle';
      }
      if (genreStr.includes('report')) {
        return 'report';
      }
      return 'book';
    }
    if (resourceType.includes('cartographic')) {
      return 'map';
    }
    if (resourceType.includes('notated music')) {
      return 'book';
    }
    if (resourceType.includes('sound recording')) {
      return 'audioRecording';
    }
    if (resourceType.includes('still image')) {
      return 'artwork';
    }
    if (resourceType.includes('moving image')) {
      return 'videoRecording';
    }
    if (resourceType.includes('software')) {
      return 'computerProgram';
    }
    if (resourceType.includes('mixed material')) {
      return 'document';
    }

    return 'book';
  }

  private extractGenres(mods: ModsRecord): string[] {
    if (!mods.genre) {
      return [];
    }
    return extractStringValues(mods.genre);
  }

  private extractTitle(mods: ModsRecord): string | null {
    if (!mods.titleInfo) {
      return null;
    }

    const titleInfo = Array.isArray(mods.titleInfo) ? mods.titleInfo[0] : mods.titleInfo;
    if (!titleInfo) {
      return null;
    }

    let title = '';

    // nonSort can be string or { '#text': string, '@space': string }
    if (titleInfo.nonSort) {
      if (typeof titleInfo.nonSort === 'string') {
        title += titleInfo.nonSort;
      } else {
        const nonSortText = getTextValue(titleInfo.nonSort);
        if (nonSortText) {
          title += nonSortText;
        }
      }
    }
    // title can be string or object
    if (titleInfo.title) {
      if (typeof titleInfo.title === 'string') {
        title += titleInfo.title;
      } else {
        const titleText = getTextValue(titleInfo.title);
        if (titleText) {
          title += titleText;
        }
      }
    }
    // subTitle can be string or object
    if (titleInfo.subTitle) {
      const subTitleText =
        typeof titleInfo.subTitle === 'string'
          ? titleInfo.subTitle
          : getTextValue(titleInfo.subTitle);
      if (subTitleText) {
        title += ': ' + subTitleText;
      }
    }
    // partNumber can be string or object
    if (titleInfo.partNumber) {
      const partNumText =
        typeof titleInfo.partNumber === 'string'
          ? titleInfo.partNumber
          : getTextValue(titleInfo.partNumber);
      if (partNumText) {
        title += '. ' + partNumText;
      }
    }
    // partName can be string or object
    if (titleInfo.partName) {
      const partNameText =
        typeof titleInfo.partName === 'string'
          ? titleInfo.partName
          : getTextValue(titleInfo.partName);
      if (partNameText) {
        title += '. ' + partNameText;
      }
    }

    return title.trim() || null;
  }

  private extractCreators(mods: ModsRecord, itemType: ItemType): Creator[] {
    if (!mods.name) {
      return [];
    }

    const names = Array.isArray(mods.name) ? mods.name : [mods.name];
    const creators: Creator[] = [];
    const validCreatorTypes = getCreatorTypesForItemType(itemType);

    for (const name of names) {
      const creator = this.parseCreator(name, validCreatorTypes);
      if (creator) {
        creators.push(creator);
      }
    }

    return creators;
  }

  private parseCreator(name: ModsName, validTypes: CreatorType[]): Creator | null {
    // Safely extract name parts using type guard
    const nameParts = extractStringValues(name.namePart);
    const fullName = nameParts.join(', ').trim();

    if (!fullName) {
      return null;
    }

    let creatorType: CreatorType = 'author';

    // Safely extract role using type guard
    if (name.role?.roleTerm) {
      const roleValue = extractTextTypeValue(name.role.roleTerm);
      const role = roleValue?.toLowerCase() || '';

      if (role.includes('editor') || role === 'edt') {
        creatorType = 'editor';
      } else if (role.includes('translator') || role === 'trl') {
        creatorType = 'translator';
      } else if (role.includes('contributor') || role === 'ctb') {
        creatorType = 'contributor';
      } else if (role.includes('illustrator') || role === 'ill') {
        creatorType = 'contributor';
      } else if (role.includes('director')) {
        creatorType = 'director';
      }
    }

    // Safely get valid creator type (no unsafe cast)
    if (!validTypes.includes(creatorType)) {
      creatorType = validTypes.length > 0 ? validTypes[0] : 'author';
    }

    const parts = fullName.split(',').map((p) => p.trim());

    if (parts.length >= 2) {
      return {
        firstName: cleanNameDates(parts.slice(1).join(' ')),
        lastName: cleanNameDates(parts[0]),
        creatorType,
      };
    }

    return {
      name: cleanNameDates(fullName),
      creatorType,
    };
  }

  private extractOriginInfo(mods: ModsRecord): {
    date?: string;
    publisher?: string;
    place?: string;
    edition?: string;
    extent?: string;
    language?: string;
  } {
    const result: {
      date?: string;
      publisher?: string;
      place?: string;
      edition?: string;
      extent?: string;
      language?: string;
    } = {};

    if (mods.originInfo) {
      const originInfos = Array.isArray(mods.originInfo) ? mods.originInfo : [mods.originInfo];

      for (const originInfo of originInfos) {
        if (!result.date) {
          result.date = this.extractDate(originInfo);
        }

        if (!result.publisher && originInfo.publisher) {
          const rawPublisher = Array.isArray(originInfo.publisher)
            ? originInfo.publisher[0]
            : originInfo.publisher;
          result.publisher = cleanPublisher(rawPublisher);
        }

        if (!result.place && originInfo.place) {
          result.place = this.extractPlace(originInfo.place);
        }

        if (!result.edition && originInfo.edition) {
          result.edition = originInfo.edition;
        }
      }
    }

    if (mods.physicalDescription?.extent) {
      result.extent = extractStringValue(mods.physicalDescription.extent) || undefined;
    }

    if (mods.language?.languageTerm) {
      result.language = extractTextTypeValue(mods.language.languageTerm) || undefined;
    }

    return result;
  }

  private extractDate(originInfo: ModsOriginInfo): string | undefined {
    // Try dateIssued first - prefer display dates without encoding/point attributes
    if (originInfo.dateIssued) {
      if (typeof originInfo.dateIssued === 'string') {
        return cleanDate(originInfo.dateIssued);
      }

      if (Array.isArray(originInfo.dateIssued)) {
        // First pass: look for display date (no encoding, no point)
        for (const d of originInfo.dateIssued) {
          if (typeof d === 'string') {
            return cleanDate(d);
          }
          if (hasAnyTextValue(d)) {
            const obj = d as Record<string, unknown>;
            // Check for @encoding/@point (Harvard format) or encoding/point (other formats)
            const hasEncoding = obj['@encoding'] || obj['encoding'];
            const hasPoint = obj['@point'] || obj['point'];
            if (!hasEncoding && !hasPoint) {
              const val = getTextValue(d);
              return val ? cleanDate(val) : undefined;
            }
          }
        }
        // Fallback to first value
        const fallback = extractStringValue(originInfo.dateIssued[0]);
        return fallback ? cleanDate(fallback) : undefined;
      }
    }

    // Fallback to dateCreated
    if (originInfo.dateCreated) {
      const val = extractStringValue(originInfo.dateCreated);
      return val ? cleanDate(val) : undefined;
    }

    return undefined;
  }

  private extractPlace(place: ModsOriginInfo['place']): string | undefined {
    if (!place) {
      return undefined;
    }

    const places = Array.isArray(place) ? place : [place];

    for (const p of places) {
      if (p.placeTerm) {
        // Use extractTextTypeValue to prefer text over code
        const placeValue = extractTextTypeValue(p.placeTerm);
        if (placeValue) {
          return capitalizePublisher(placeValue);
        }
      }
    }

    return undefined;
  }

  private extractIdentifiers(mods: ModsRecord): {
    isbn?: string;
    issn?: string;
    doi?: string;
    oclc?: string;
  } {
    const result: {
      isbn?: string;
      issn?: string;
      doi?: string;
      oclc?: string;
    } = {};

    if (!mods.identifier) {
      return result;
    }

    const identifiers = Array.isArray(mods.identifier) ? mods.identifier : [mods.identifier];

    for (const id of identifiers) {
      // Get type from @type or type attribute
      const typeAttr = getTypeAttr(id);
      // Get value from _ or #text
      const textValue = getTextValue(id);

      if (!typeAttr || !textValue) {
        continue;
      }

      const type = typeAttr.toLowerCase();
      const value = textValue.trim();

      if (type === 'isbn' && !result.isbn) {
        result.isbn = value;
      } else if (type === 'issn' && !result.issn) {
        result.issn = value;
      } else if (type === 'doi' && !result.doi) {
        result.doi = value;
      } else if (type === 'oclc' && !result.oclc) {
        result.oclc = value;
      }
    }

    return result;
  }

  private extractAbstract(mods: ModsRecord): string | undefined {
    if (!mods.abstract) {
      return undefined;
    }

    return extractStringValue(mods.abstract) || undefined;
  }

  private extractSubjects(mods: ModsRecord): string[] {
    const subjects: string[] = [];

    // subject can be undefined, an empty object {}, or an array
    if (!mods.subject || !Array.isArray(mods.subject)) {
      return subjects;
    }

    for (const subj of mods.subject) {
      // Skip null/undefined entries
      if (!subj) continue;

      if (subj.topic) {
        subjects.push(...extractStringValues(subj.topic));
      }
      if (subj.geographic) {
        subjects.push(...extractStringValues(subj.geographic));
      }
      if (subj.temporal) {
        subjects.push(...extractStringValues(subj.temporal));
      }
    }

    return [...new Set(subjects)].slice(0, 10);
  }

  private extractUrls(mods: ModsRecord): {
    accessUrl?: string;
    thumbnail?: string;
  } {
    const result: {
      accessUrl?: string;
      thumbnail?: string;
    } = {};

    if (!mods.location) {
      return result;
    }

    for (const loc of mods.location) {
      // Skip null entries in location array
      if (!loc || !loc.url) {
        continue;
      }

      const urls = Array.isArray(loc.url) ? loc.url : [loc.url];

      for (const url of urls) {
        if (typeof url === 'string') {
          if (!result.accessUrl) {
            result.accessUrl = url;
          }
        } else if (hasAnyTextValue(url)) {
          const urlValue = getTextValue(url);
          if (!urlValue) continue;

          const obj = url as Record<string, unknown>;
          // Handle both @access (Harvard format) and access (other formats)
          const accessAttr = (obj['@access'] || obj['access']) as string | undefined;

          if (accessAttr === 'raw object' && !result.accessUrl) {
            result.accessUrl = urlValue;
          } else if (accessAttr === 'preview' && !result.thumbnail) {
            result.thumbnail = urlValue;
          } else if (!result.accessUrl && !accessAttr) {
            // Default: use as access URL if no access type specified
            result.accessUrl = urlValue;
          }
        }
      }
    }

    return result;
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const harvardLibraryService = new HarvardLibraryService();
export default harvardLibraryService;
